import React, { useState, useEffect } from 'react';
import { PageSection, SiteData, YachtItem } from '../../../types';
import { dbService } from '../../../services/firebase';
import { ArrowRight, Anchor, ShieldAlert, Plus } from 'lucide-react';
import { isDarkBackground, getOptimalTextColor, getOptimalMutedTextColor } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
  onNavigate?: (slug: string) => void;
  onSelectYacht?: (yacht: YachtItem) => void;
}

export const YachtFleetSection: React.FC<SectionProps> = ({
  section,
  siteData,
  onNavigate,
  onSelectYacht,
}) => {
  const content = section.content || {};
  const style = section.style || {};

  const badge = content.badge || 'LÜKS YAT VE GULET FİLOSU';
  const title = content.title || 'Masmavi Bir Seyir İçin Seçkin Teknelerimiz';
  const description =
    content.description ||
    'Filomuzda yer alan tam donanımlı teknelerimiz ile unutulmaz bir mavi yolculuk planlayın.';

  const [filterType, setFilterType] = useState<string>('ALL');
  const [yachtList, setYachtList] = useState<YachtItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;
    const fetchFleet = async () => {
      try {
        setIsLoading(true);
        const data = await dbService.getYachts(siteData.id);
        if (isMounted) {
          setYachtList(data || []);
        }
      } catch (err) {
        console.warn('Fleet section fetch error:', err);
        if (isMounted) setYachtList([]);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetchFleet();
    return () => {
      isMounted = false;
    };
  }, [siteData.id]);

  const uniqueTypes = Array.from(new Set(yachtList.map((y) => y.type).filter(Boolean)));
  const filterOptions = ['ALL', ...uniqueTypes];

  const filteredYachts = yachtList.filter((yacht) => {
    if (filterType === 'ALL') return true;
    return yacht.type === filterType;
  });

  const bgColor = style.backgroundColor || '#F8FAFC';
  const isDark = isDarkBackground(bgColor);
  const textColor = style.textColor || getOptimalTextColor(bgColor);
  const mutedTextColor = getOptimalMutedTextColor(bgColor);
  const accentColor = siteData.settings?.accentColor || '#0284C7';

  return (
    <section
      className="py-20 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className="max-w-7xl mx-auto space-y-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-3">
          {badge && (
            <div
              className={`inline-block px-3.5 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider ${
                isDark ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-500/10 text-cyan-700'
              }`}
            >
              {badge}
            </div>
          )}
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight">{title}</h2>
          {description && (
            <p className="text-sm sm:text-base leading-relaxed max-w-2xl mx-auto" style={{ color: mutedTextColor }}>
              {description}
            </p>
          )}

          {/* Filter Pills - only shown if yachts exist and have multiple categories */}
          {yachtList.length > 0 && uniqueTypes.length > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4 flex-wrap">
              {filterOptions.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setFilterType(t)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                    filterType === t
                      ? 'shadow-md scale-105'
                      : isDark
                      ? 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-white/10'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200/80'
                  }`}
                  style={
                    filterType === t
                      ? { backgroundColor: accentColor, color: getOptimalTextColor(accentColor) }
                      : undefined
                  }
                >
                  {t === 'ALL' ? 'Tüm Filo' : t}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Yacht Cards Grid OR Empty Fleet State */}
        {isLoading ? (
          <div className="py-16 text-center text-slate-400">
            <div className="inline-block w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-xs font-medium">Filo verileri yükleniyor...</p>
          </div>
        ) : yachtList.length === 0 ? (
          <div
            className={`max-w-xl mx-auto p-8 rounded-3xl border text-center space-y-4 ${
              isDark ? 'bg-slate-900/60 border-white/10 text-slate-300' : 'bg-white border-slate-200 text-slate-600 shadow-sm'
            }`}
          >
            <div className="w-14 h-14 rounded-2xl bg-teal-500/10 text-teal-600 flex items-center justify-center mx-auto">
              <Anchor className="w-7 h-7" />
            </div>
            <div className="space-y-1.5">
              <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Henüz Filoya Yat Eklenmedi
              </h3>
              <p className="text-xs leading-relaxed max-w-md mx-auto text-slate-400">
                Bu web sitesinde henüz listelenmiş bir tekne bulunmuyor. Yönetim panelinizdeki <strong>Yat Yönetimi</strong> ekranından ilk teknenizi kaydederek misafirlerinize sunabilirsiniz.
              </p>
            </div>
            {onNavigate && (
              <button
                type="button"
                onClick={() => onNavigate('#yacht-fleet')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-xl text-xs font-bold transition shadow-sm"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Yat Yönetimine Git</span>
              </button>
            )}
          </div>
        ) : filteredYachts.length === 0 ? (
          <div className="py-12 text-center text-slate-400 space-y-2">
            <ShieldAlert className="w-8 h-8 mx-auto text-slate-400" />
            <p className="text-sm font-semibold">Bu kategoride gösterilecek yat bulunamadı.</p>
            <button
              type="button"
              onClick={() => setFilterType('ALL')}
              className="text-xs font-bold text-teal-600 hover:underline"
            >
              Tüm filoyu göster
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredYachts.map((yacht) => (
              <div
                key={yacht.id}
                className={`rounded-3xl overflow-hidden border shadow-xs hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1.5 flex flex-col justify-between group ${
                  isDark
                    ? 'bg-slate-900 border-white/10 text-white'
                    : 'bg-white border-slate-200/80 text-slate-900'
                }`}
              >
                {/* Image & Price Tag */}
                <div className="relative aspect-16/10 overflow-hidden bg-slate-900">
                  <img
                    src={yacht.coverImage || 'https://images.unsplash.com/photo-1569263979104-865ab7cd8d17?auto=format&fit=crop&w=1200&q=80'}
                    alt={yacht.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4 bg-slate-950/80 backdrop-blur-md text-white text-[11px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
                    <Anchor className="w-3.5 h-3.5 text-cyan-400" />
                    <span>{yacht.type}</span>
                  </div>
                  {(yacht.portLocation || yacht.basePort) && (
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md text-slate-900 text-[10px] font-extrabold px-3 py-1 rounded-full shadow">
                      {yacht.portLocation || yacht.basePort}
                    </div>
                  )}
                </div>

                {/* Card Body */}
                <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
                  <div className="space-y-2">
                    <h3
                      className={`font-extrabold text-lg transition leading-snug ${
                        isDark ? 'text-white' : 'text-slate-900'
                      }`}
                    >
                      {yacht.name}
                    </h3>
                    {yacht.description && (
                      <p className={`text-xs line-clamp-2 leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {yacht.description}
                      </p>
                    )}

                    {/* Specs Row */}
                    <div className={`grid grid-cols-3 gap-2 pt-2 border-t text-center ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
                      <div className={`p-2 rounded-xl ${isDark ? 'bg-slate-800/80' : 'bg-slate-50'}`}>
                        <div className="text-[10px] font-bold text-slate-400">Boy</div>
                        <div className={`text-xs font-extrabold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                          {yacht.lengthMeters || yacht.length || '-'}m
                        </div>
                      </div>
                      <div className={`p-2 rounded-xl ${isDark ? 'bg-slate-800/80' : 'bg-slate-50'}`}>
                        <div className="text-[10px] font-bold text-slate-400">Kapasite</div>
                        <div className={`text-xs font-extrabold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                          {yacht.capacity} Kişi
                        </div>
                      </div>
                      <div className={`p-2 rounded-xl ${isDark ? 'bg-slate-800/80' : 'bg-slate-50'}`}>
                        <div className="text-[10px] font-bold text-slate-400">Kabin</div>
                        <div className={`text-xs font-extrabold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                          {yacht.cabins} Kabin
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Price & Action Button */}
                  <div className={`pt-4 border-t flex items-center justify-between ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase">Günlük Fiyat</div>
                      <div className={`text-lg font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {yacht.currency === 'USD' ? '$' : yacht.currency === 'TRY' ? '₺' : '€'}
                        {(yacht.pricePerDay || 0).toLocaleString()}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        if (onSelectYacht) onSelectYacht(yacht);
                        else if (onNavigate) onNavigate('/rezervasyon');
                      }}
                      className="px-4 py-2.5 rounded-xl text-xs font-bold shadow transition flex items-center gap-1.5 hover:opacity-90 cursor-pointer"
                      style={{ backgroundColor: accentColor, color: getOptimalTextColor(accentColor) }}
                    >
                      <span>Rezervasyon</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
